import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Handshake, Scale, Lightbulb, TrendingUp, 
  Users, Zap, FileCheck, Award, ArrowRight 
} from 'lucide-react';

const guidingPrinciples = [
  { icon: Shield, title: "Customer Empowerment", color: "green", delay: 0.05 },
  { icon: Handshake, title: "Responsible Borrowing", color: "yellow", delay: 0.1 },
  { icon: Scale, title: "Ethical & Transparent", color: "red", delay: 0.15 },
  { icon: Lightbulb, title: "Service Improvement", color: "green", delay: 0.2 },
  { icon: TrendingUp, title: "Economic Development", color: "yellow", delay: 0.25 },
];

const trustBadges = [
  { icon: Users, title: "Trusted by Thousands of Growing Businesses", detail: "India's premier financial network.", color: "green", delay: 0.3 },
  { icon: Zap, title: "Ultra-Fast Processing", detail: "Disbursement within 24-48 hours.", color: "yellow", delay: 0.35 },
  { icon: FileCheck, title: "Zero Hidden Costs", detail: "100% Transparency in every contract.", color: "red", delay: 0.4 },
  { icon: Award, title: "#1 Trusted Financial Partner 2024", detail: "Recognized for excellence", color: "green", delay: 0.45 },
];

const darkColorClasses = {
  green: "bg-green-900/60 text-green-300 border-green-700 group-hover:bg-green-800/80 group-hover:text-green-200 group-hover:border-green-600",
  yellow: "bg-yellow-900/60 text-yellow-300 border-yellow-700 group-hover:bg-yellow-800/80 group-hover:text-yellow-200 group-hover:border-yellow-600",
  red: "bg-red-900/60 text-red-300 border-red-700 group-hover:bg-red-800/80 group-hover:text-red-200 group-hover:border-red-600",
};

const OurStrategicMission = () => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    const section = document.getElementById('strategic-mission');
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-card {
          opacity: 0;
          animation: fadeSlideUp 0.5s cubic-bezier(0.2, 0.9, 0.4, 1.1) forwards;
        }
        .hover-scale-icon:hover .icon-wrapper {
          transform: scale(1.05) rotate(1deg);
        }
        .btn-glow {
          transition: all 0.3s ease;
        }
        .btn-glow:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -5px rgba(34,197,94,0.4);
        }
      `}</style>

      <section id="strategic-mission" className="py-12 relative overflow-hidden bg-gradient-to-br from-gray-900 via-green-950 to-gray-900">
        {/* Subtle dark blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-800/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-800/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left Column */}
            <div className="space-y-5">
              <div className={`${animated ? 'animate-card' : 'opacity-0'}`} style={{ animationDelay: '0s' }}>
                <span className="inline-block px-3 py-1 bg-green-700/60 text-green-200 text-xs font-semibold rounded-full backdrop-blur-sm border border-green-600/40">
                  Our Strategic Mission
                </span>
              </div>
              <div className={`${animated ? 'animate-card' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
                <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
                  Empowering Your
                  <span className="text-green-400 block">Financial Future.</span>
                </h2>
              </div>
              <div className={`${animated ? 'animate-card' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                <p className="text-gray-300 text-base leading-relaxed">
                  Guiding customers toward responsible lending solutions that foster growth, stability, and long-term success.
                </p>
              </div>
              <div>
                <div className={`flex items-center gap-2 mb-3 ${animated ? 'animate-card' : 'opacity-0'}`} style={{ animationDelay: '0.25s' }}>
                  <Shield className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-semibold text-gray-200">Guiding Principles</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {guidingPrinciples.map((principle, idx) => (
                    <div
                      key={idx}
                      className={`group flex items-center gap-3 p-3 rounded-xl bg-gray-800/50 backdrop-blur-sm border border-gray-700 transition-all duration-300 hover:bg-gray-800/80 hover:border-gray-600 cursor-default hover-scale-icon ${
                        animated ? 'animate-card' : 'opacity-0'
                      }`}
                      style={{ animationDelay: `${0.3 + principle.delay}s` }}
                    >
                      <div className={`p-2 rounded-lg ${darkColorClasses[principle.color]} transition-all duration-300 icon-wrapper`}>
                        <principle.icon className="w-4 h-4" />
                      </div>
                      <span className="text-gray-200 font-medium text-sm group-hover:text-white">
                        {principle.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`pt-3 ${animated ? 'animate-card' : 'opacity-0'}`} style={{ animationDelay: '0.55s' }}>
                <Link
                  to="/contact"
                  className="btn-glow inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-400 hover:from-green-400 hover:to-green-500 text-gray-900 font-semibold px-6 py-2.5 rounded-lg transition-all duration-300 hover:gap-3 group shadow-lg"
                >
                  Get Started Today
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            <div>
              <div className={`flex items-center gap-2 mb-3 ${animated ? 'animate-card' : 'opacity-0'}`} style={{ animationDelay: '0.35s' }}>
                <Award className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-semibold text-gray-200">Why Our Clients Trust Us</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {trustBadges.map((badge, idx) => (
                  <div
                    key={idx}
                    className={`group bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700 transition-all duration-300 hover:-translate-y-1 hover:bg-gray-800/80 hover:border-gray-600 cursor-default hover-scale-icon ${
                      animated ? 'animate-card' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${0.4 + badge.delay}s` }}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className={`p-2.5 rounded-full ${darkColorClasses[badge.color]} mb-2 transition-all duration-300 icon-wrapper`}>
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <h3 className="font-bold text-white text-sm mb-1 group-hover:text-gray-200">
                        {badge.title}
                      </h3>
                      <p className="text-xs text-gray-400 group-hover:text-gray-300">
                        {badge.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent" />
      </section>
    </>
  );
};

export default OurStrategicMission;


